import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Folder, FolderPlus, ChevronRight, ChevronDown, Edit, Trash2, MoreHorizontal, FileText, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const FolderTree = ({ clientId, onSelectFolder, selectedFolderId }) => {
  const { token, isAdmin, isCliente } = useAuth();
  const [folders, setFolders] = useState([]);
  const [trademarkCounts, setTrademarkCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedFolders, setExpandedFolders] = useState(new Set());

  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [parentFolderForCreate, setParentFolderForCreate] = useState(null);
  const [folderName, setFolderName] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (clientId) {
      fetchFolders();
    }
  }, [clientId]);

  const fetchFolders = async () => {
    try {
      const res = await axios.get(`${API}/clients/${clientId}/folders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFolders(res.data);

      // Get trademark counts per folder
      const tmRes = await axios.get(`${API}/trademarks?client_id=${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const counts = { null: 0 };
      tmRes.data.forEach(tm => {
        const folderId = tm.folder_id || null;
        counts[folderId] = (counts[folderId] || 0) + 1;
      });
      setTrademarkCounts(counts);
    } catch (error) {
      console.error('Error fetching folders:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (folderId) => {
    setExpandedFolders(prev => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  };

  const handleCreateFolder = async () => {
    if (!folderName.trim()) {
      toast.error('El nombre es requerido');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/clients/${clientId}/folders`, {
        client_id: clientId,
        name: folderName.trim(),
        parent_id: parentFolderForCreate
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Carpeta creada');
      setCreateModalOpen(false);
      setFolderName('');
      setParentFolderForCreate(null);
      fetchFolders();
    } catch (error) {
      console.error('Error creating folder:', error);
      toast.error(error.response?.data?.detail || 'Error al crear carpeta');
    } finally {
      setSaving(false);
    }
  };

  const handleEditFolder = async () => {
    if (!folderName.trim() || !selectedFolder) return;

    setSaving(true);
    try {
      await axios.put(`${API}/folders/${selectedFolder.id}`, {
        name: folderName.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Carpeta actualizada');
      setEditModalOpen(false);
      setSelectedFolder(null);
      setFolderName('');
      fetchFolders();
    } catch (error) {
      console.error('Error updating folder:', error);
      toast.error('Error al actualizar carpeta');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFolder = async () => {
    if (!selectedFolder) return;

    setDeleting(true);
    try {
      await axios.delete(`${API}/folders/${selectedFolder.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Carpeta eliminada');
      setDeleteDialogOpen(false);
      setSelectedFolder(null);

      // If deleted folder was selected, clear selection
      if (selectedFolderId === selectedFolder.id) {
        onSelectFolder(null);
      }

      fetchFolders();
    } catch (error) {
      console.error('Error deleting folder:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar carpeta');
    } finally {
      setDeleting(false);
    }
  };

  const openCreateModal = (parentId = null) => {
    setParentFolderForCreate(parentId);
    setFolderName('');
    setCreateModalOpen(true);
  };

  const openEditModal = (folder) => {
    setSelectedFolder(folder);
    setFolderName(folder.name);
    setEditModalOpen(true);
  };

  const openDeleteDialog = (folder) => {
    setSelectedFolder(folder);
    setDeleteDialogOpen(true);
  };

  // Build folder tree structure
  const buildTree = (parentId = null) => {
    return folders
      .filter(f => f.parent_id === parentId)
      .sort((a, b) => a.order - b.order);
  };

  const renderFolder = (folder, depth = 0) => {
    const children = buildTree(folder.id);
    const hasChildren = children.length > 0;
    const isExpanded = expandedFolders.has(folder.id);
    const isSelected = selectedFolderId === folder.id;
    const count = trademarkCounts[folder.id] || 0;
    const canCreateSubfolder = folder.level < 2;

    return (
      <div key={folder.id} data-testid={`folder-${folder.id}`}>
        <div
          className={`flex items-center gap-2 py-1.5 px-2 rounded-md cursor-pointer group ${isSelected ? 'bg-blue-100 text-blue-900' : 'hover:bg-gray-100'
            }`}
          style={{ paddingLeft: `${8 + depth * 16}px` }}
          onClick={() => onSelectFolder(folder.id)}
        >
          {hasChildren ? (
            <button
              onClick={(e) => { e.stopPropagation(); toggleExpand(folder.id); }}
              className="p-0.5 hover:bg-gray-200 rounded"
            >
              {isExpanded ? (
                <ChevronDown className="h-3.5 w-3.5 text-gray-500" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5 text-gray-500" />
              )}
            </button>
          ) : (
            <div className="w-4" />
          )}

          <Folder className={`h-4 w-4 ${isSelected ? 'text-blue-600' : 'text-indigo-500'}`} />

          <span className={`flex-1 text-sm truncate ${isSelected ? 'font-medium' : ''}`}>
            {folder.name}
          </span>

          {count > 0 && (
            <span className={`text-xs ml-2 px-1.5 py-0.5 rounded-full ${isSelected ? 'bg-blue-200 text-blue-800' : 'bg-gray-100 text-gray-600'}`}>
              {count}
            </span>
          )}

          {!isCliente() && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  onClick={(e) => e.stopPropagation()}
                  className="p-1 opacity-0 group-hover:opacity-100 hover:bg-gray-200 rounded"
                >
                  <MoreHorizontal className="h-3.5 w-3.5 text-gray-500" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {canCreateSubfolder && (
                  <DropdownMenuItem onClick={() => openCreateModal(folder.id)}>
                    <FolderPlus className="h-4 w-4 mr-2" />
                    Nueva subcarpeta
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => openEditModal(folder)}>
                  <Edit className="h-4 w-4 mr-2" />
                  Renombrar
                </DropdownMenuItem>
                {isAdmin() && (
                  <DropdownMenuItem
                    onClick={() => openDeleteDialog(folder)}
                    className="text-red-600"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Eliminar
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {isExpanded && hasChildren && (
          <div>
            {children.map(child => renderFolder(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
      </div>
    );
  }

  const rootFolders = buildTree(null);
  const unassignedCount = trademarkCounts[null] || 0;

  return (
    <div className="space-y-2">
      {/* Header */}
      {/* Header */}
      <div className="flex items-center justify-between px-2 mb-2">
        <span className="text-xs font-semibold text-gray-900 uppercase tracking-wider">
          Portafolios
        </span>
        {!isCliente() && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => openCreateModal(null)}
            className="h-6 w-6 text-gray-500 hover:text-gray-900"
            title="Nuevo Portafolio"
            data-testid="create-folder-btn"
          >
            <FolderPlus className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* All / Unassigned */}
      <div
        className={`flex items-center gap-2 py-1.5 px-2 rounded-md cursor-pointer ${selectedFolderId === null ? 'bg-blue-100 text-blue-900' : 'hover:bg-gray-100'
          }`}
        onClick={() => onSelectFolder(null)}
        data-testid="folder-all"
      >
        <div className="w-4" />
        <FileText className={`h-4 w-4 ${selectedFolderId === null ? 'text-blue-600' : 'text-gray-500'}`} />
        <span className={`flex-1 text-sm ${selectedFolderId === null ? 'font-medium' : ''}`}>
          Todas
        </span>
        {unassignedCount > 0 && (
          <span className={`text-xs ml-2 px-1.5 py-0.5 rounded-full ${selectedFolderId === null ? 'bg-blue-200 text-blue-800' : 'bg-gray-100 text-gray-600'}`}>
            {unassignedCount}
          </span>
        )}
      </div>

      {/* Folder tree */}
      {rootFolders.map(folder => renderFolder(folder, 0))}

      {/* Create Folder Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderPlus className="h-5 w-5" />
              Nueva Carpeta
            </DialogTitle>
            <DialogDescription>
              {parentFolderForCreate
                ? 'Crear una subcarpeta dentro del portafolio seleccionado'
                : 'Crear un nuevo portafolio para organizar las marcas'
              }
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <Label>Nombre de la carpeta</Label>
            <Input
              value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              placeholder="Ej: Marcas Registradas, Patentes 2024..."
              className="mt-1"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && !saving && handleCreateFolder()}
              data-testid="folder-name-input"
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateModalOpen(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={handleCreateFolder} disabled={saving || !folderName.trim()} data-testid="create-folder-submit">
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creando...
                </>
              ) : (
                'Crear'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Folder Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5" />
              Renombrar Carpeta
            </DialogTitle>
          </DialogHeader>

          <div className="py-4">
            <Label>Nombre de la carpeta</Label>
            <Input
              value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              className="mt-1"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && !saving && handleEditFolder()}
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModalOpen(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={handleEditFolder} disabled={saving || !folderName.trim()}>
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Guardando...
                </>
              ) : (
                'Guardar'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar carpeta?</AlertDialogTitle>
            <AlertDialogDescription>
              La carpeta &ldquo;{selectedFolder?.name}&rdquo; será eliminada. Las marcas dentro de ella
              quedarán sin carpeta asignada.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteFolder}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Eliminando...
                </>
              ) : (
                'Eliminar'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
